import { Injectable } from '@angular/core';
import { map, Observable } from 'rxjs';
import { fabric } from 'fabric';
import { customFabricFields } from '../../common/interfaces/sync-service-interface';
import { SessionsSyncService } from '../../services/sessions-sync.service';
import { FabricObject } from '../session/items-canvas/board-items/board-items-manager';
import { BoardItemsFactory } from '../session/items-canvas/board-items/board-items-factory';
import { BoardItemsUpdate, SpaceRepository } from '../../state/space.repository';
import { BoardItemsRepository } from '../../state/board-items-repository.interface';
import { BoardItem } from '../session/items-canvas/board-items/board.item';
import { BoardItemsStackingOrderManager } from '../session/items-canvas/board-items-stacking-order-manager';
import { isLineOrArrowShape, isOldStickyNote } from '../session/wb-canvas/fabric-utils';
import { FlagsService, FLAGS } from '../../services/flags.service';
import { TelemetryService } from '../../services/telemetry.service';
import { BoardItemsEncoderDecoder } from '../../services/board-items-encoder-decoder.service';
import {
  isBoardItem,
  isValidYCanvasItem,
  isYObject,
  YCanvasItemObject,
  YObject,
} from './y-session';
import {
  isScreenShareAnnotationsSpaceID,
  SCREEN_SHARE_FABRIC_OBJECT_SERIALIZATION_PRECISION,
} from './session-call-user/screen-share-annotations/screen-share-annotations.utils';

export enum YOperationType {
  Add = 'Add',
  Modify = 'Modify',
  Remove = 'Remove',
}

const INVALID_CANVAS_OBJECT = 'invalid canvas object';

@Injectable({
  providedIn: 'root',
})
export class YSessionBoardItemsAdapter {
  constructor(
    private sessionSyncService: SessionsSyncService,
    private boardItemsFactory: BoardItemsFactory,
    private spaceRepo: SpaceRepository,
    private boardItemsStackingOrderManager: BoardItemsStackingOrderManager,
    private flagsService: FlagsService,
    private telemetryService: TelemetryService,
    private boardItemsEncoderDecoder: BoardItemsEncoderDecoder,
  ) {}

  // This will be a slower version of boardItems$ as it takes more time to decode encoded objects
  public decodedBoardItems$(
    spaceId: string,
    frameUid: string,
  ): Observable<BoardItemsUpdate<FabricObject>> {
    return this.boardItems$(spaceId, frameUid).pipe(
      map((boardItemsObject) => ({
        ...boardItemsObject,
        objects: this.boardItemsEncoderDecoder.decodeFabricItems(boardItemsObject.objects),
      })),
    );
  }

  public boardItems$(
    spaceId: string,
    frameUid: string,
    boardItemsRepository?: BoardItemsRepository,
  ): Observable<BoardItemsUpdate<FabricObject>> {
    return new Observable((subscriber) => {
      const sourceRepo = boardItemsRepository ?? this.spaceRepo;
      const boardItemsSubscription = sourceRepo
        .boardItems$(spaceId, frameUid)
        .subscribe(({ objects, lastActionIsLocal, lastUpdateLocal }) => {
          // Transforming YObject and YCanvasItemObject to FabricObject before processing the updates to have a unified structure to deal with
          const parsedFabricObjects = this.parseYObjectsAndYCanvasObjects(objects, frameUid);
          subscriber.next({ objects: parsedFabricObjects, lastActionIsLocal, lastUpdateLocal });
        });
      // teardown logic
      return () => {
        boardItemsSubscription.unsubscribe();
      };
    });
  }

  public parseYObjectsAndYCanvasObjects(
    objects: (YCanvasItemObject | YObject)[],
    boardUid: string,
  ): FabricObject[] {
    const yObjects: YObject[] = [];
    const yCanvasObjects: YCanvasItemObject[] = [];
    for (const object of objects) {
      if (isValidYCanvasItem(object)) {
        yCanvasObjects.push(object);
      } else if (isYObject(object)) {
        yObjects.push(object);
      } else {
        this.telemetryService.errorEvent(INVALID_CANVAS_OBJECT, { object, boardUid });
      }
    }

    const boardItems = this.parseYCanvasObjects(yCanvasObjects, boardUid);

    const fabricObjects = yObjects.map((object, i) => {
      let fabricObject: YObject | BoardItem = object;
      (fabricObject as any).boardUid = boardUid;
      // Parsing migrated fabric objects to the new structure
      if (isOldStickyNote(object) || isLineOrArrowShape(object)) {
        // If the sticky note is an old one, the order should point to the index, in case no explicit order is specified
        // as the old way in processing stacking relied on the array order instead of storing it
        object['order'] = object['order'] ?? i;
        fabricObject = this.boardItemsFactory.createBoardItemFromFabricObject(object, boardUid);
      }
      return fabricObject;
    });

    return this.boardItemsStackingOrderManager.getFabricObjectsWithInsertionOrder(
      boardItems,
      fabricObjects,
    ) as FabricObject[];
  }

  public parseYCanvasObjects(yCanvasObjects: YCanvasItemObject[], boardUid: string): BoardItem[] {
    return yCanvasObjects.map((yCanvasObject) =>
      this.boardItemsFactory.createBoardItemFromFrameItem(yCanvasObject, boardUid),
    );
  }

  public modifyInYSession(sessionId: string, updatedObjects: FabricObject[]) {
    return this.processYSessionUpdate(sessionId, updatedObjects, YOperationType.Modify);
  }

  public addToYSession(sessionId: string, updatedObjects: FabricObject[]) {
    return this.processYSessionUpdate(sessionId, updatedObjects, YOperationType.Add);
  }

  public removeFromYSession(sessionId: string, updatedObjects: FabricObject[]) {
    return this.processYSessionUpdate(sessionId, updatedObjects, YOperationType.Remove);
  }

  /**
   * Adds plain YObjects and YCanvasItemObjects directly to the Y session without Fabric transformation.
   * This is a synchronous operation that bypasses the need to enliven Fabric objects.
   *
   * @param sessionId - The session ID
   * @param updatedFabricObjects - Map of boardUid to plain YObject arrays
   * @param updatedCanvasItems - Map of boardUid to plain YCanvasItemObject arrays
   * @param operation - The type of operation (Add, Modify, Remove)
   */
  public addPlainObjectsToYSession(
    sessionId: string,
    updatedFabricObjects: Map<string | undefined, YObject[]>,
    updatedCanvasItems: Map<string | undefined, YCanvasItemObject[]>,
    operation: YOperationType,
  ) {
    const ySession = this.sessionSyncService.getYSessionById(sessionId);

    const fabricUpdateOperation = {
      [YOperationType.Add]: ySession.addObjectsToDifferentBoards.bind(ySession),
      [YOperationType.Modify]: ySession.modifyObjects.bind(ySession),
      [YOperationType.Remove]: ySession.removeObjects.bind(ySession),
    }[operation];

    if (updatedFabricObjects.size) {
      fabricUpdateOperation(updatedFabricObjects);
    }

    const canvasItemsUpdateOperation = {
      [YOperationType.Add]: ySession.addCanvasItems.bind(ySession),
      [YOperationType.Modify]: ySession.modifyCanvasItems.bind(ySession),
      [YOperationType.Remove]: ySession.removeCanvasItems.bind(ySession),
    }[operation];

    if (updatedCanvasItems.size) {
      for (const [boardUid, objects] of updatedCanvasItems.entries()) {
        boardUid && canvasItemsUpdateOperation(boardUid, objects);
      }
    }

    if (updatedFabricObjects.size || updatedCanvasItems.size) {
      this.sessionSyncService.calcDiffAndSendUpdate(
        sessionId,
        // if updates are not already buffered before applying them, we apply a throttle before sending calculating the difference and sending the y update.
        !this.flagsService.isFlagEnabled(FLAGS.BUFFER_FABRIC_UPDATE_ON_APPLY_UPDATE_LEVEL),
      );
    }
  }

  processYSessionUpdate(
    sessionId: string,
    updatedObjects: FabricObject[],
    operation: YOperationType,
  ) {
    if (updatedObjects.length === 0) {
      return;
    }
    const isScreenShareSession = isScreenShareAnnotationsSpaceID(sessionId);
    const { updatedFabricObjects, updatedCanvasItems } = this.getCanvasItemsAndFabricObjectsMaps(
      updatedObjects,
      isScreenShareSession,
    );

    this.addPlainObjectsToYSession(sessionId, updatedFabricObjects, updatedCanvasItems, operation);
  }

  private getCanvasItemsAndFabricObjectsMaps(
    updatedObjects: FabricObject[],
    isScreenShareSession = false,
  ) {
    // Key: boardUid, Value: updated objects
    const updatedFabricObjects = new Map<string | undefined, YObject[]>();
    const updatedCanvasItems = new Map<string | undefined, YCanvasItemObject[]>();

    for (const object of updatedObjects) {
      if (isBoardItem(object) && object.isStoredAsFrameItem?.()) {
        this.addObjectToMapArray(updatedCanvasItems, object.toFrameItem(), object.boardUid);
      } else {
        this.addObjectToMapArray(
          updatedFabricObjects,
          this.convertFabricObjectToObject(object, customFabricFields, isScreenShareSession),
          object.boardUid,
        );
      }
    }

    return {
      updatedFabricObjects,
      updatedCanvasItems,
    };
  }

  addObjectToMapArray(
    mapToAddTo: Map<string | undefined, any[]>,
    object: YObject | YCanvasItemObject,
    boardUid: string,
  ) {
    if (mapToAddTo.has(boardUid)) {
      mapToAddTo.get(boardUid)?.push(object);
    } else {
      mapToAddTo.set(boardUid, [object]);
    }
  }

  // For some reason, when `strokeWidth` is 1, toObject method makes it undefined,
  // so we need to fix it in the resulting object.
  private convertFabricObjectToObject(
    object: fabric.Object,
    customFields: string[],
    isScreenShareSession = false,
  ) {
    const originalObject = isScreenShareSession
      ? this.withHighPrecisionFabricSerialization(() => object.toObject(customFields))
      : object.toObject(customFields);
    originalObject.strokeWidth = object.strokeWidth;
    return originalObject;
  }

  // NUM_FRACTION_DIGITS which is equal to 2, is used to round the coords to 2 decimal places.
  // In screen share canvas,the coords are normalized to 0..1, so they get rounded too aggressively affecting object position.
  // This method sets the NUM_FRACTION_DIGITS to a higher precision value, so the obejct position is not affected after the serialization.
  private withHighPrecisionFabricSerialization<T>(callback: () => T): T {
    const fabricObjectClass = fabric.Object as unknown as { NUM_FRACTION_DIGITS?: number };
    const previousPrecision = fabricObjectClass.NUM_FRACTION_DIGITS;

    fabricObjectClass.NUM_FRACTION_DIGITS = SCREEN_SHARE_FABRIC_OBJECT_SERIALIZATION_PRECISION;
    try {
      return callback();
    } finally {
      // reset NUM_FRACTION_DIGITS to the original value
      fabricObjectClass.NUM_FRACTION_DIGITS = previousPrecision;
    }
  }
}
